// src/components/BoxesPage.tsx
import React, { useEffect, useState, useRef, useCallback } from "react";
import styled from "styled-components";
import DynamicBoxes from "./DynamicBoxes";
import { BoxType } from "../utils/boxTypes";
import BoxesPageHeading from "./BoxesPageHeading";
import Footer from "./Footer";
import localization from "../services/localization";
import HeroComponent from "./HeroComponent";
import { AppDispatch, RootState } from "../app/store";
import { useDispatch, useSelector } from "react-redux";
import HowItWorksPage from "./HowItWorks";
import { useNavigate } from "react-router-dom";
import { Platform, PromoSlider } from "keycloakauthmodule";
import { handlePageSelection } from "../features/pageSlice";

const BoxesPageWrapper = styled.div`
  padding: 20px;
  color: #fff;
`;

// Styled component for additional vertical space
const Spacer = styled.div`
  height: 100px;
`;

const Spacer10 = styled.div`
  height: 10px;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
`;

interface ScrollButtonProps {
  $gradientColors: string[];
}

const ScrollButton = styled.button<ScrollButtonProps>`
  font-family: "BernierShade", sans-serif;
  font-size: 18px;
  font-weight: normal;
  letter-spacing: normal;
  text-align: center;
  padding: 12px 24px;
  border: none;
  cursor: pointer;
  border-radius: 5px;
  background: linear-gradient(to bottom, ${(props) => props.$gradientColors.join(", ")});
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  border: 2px solid ${(props) => props.$gradientColors[0]};
  transition: all 0.3s ease-in-out;

  &:hover {
    background: linear-gradient(to bottom, ${(props) => props.$gradientColors.reverse().join(", ")});
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
    border-color: ${(props) => props.$gradientColors[0]};
  }

  @media (max-width: 768px) {
    font-size: 16px;
    padding: 10px 20px;
  }
`;

const WidgetSection = styled.div`
  width: 100%;
  margin-top: 16px;
`;

const WidgetFrame = styled.iframe`
  width: 100%;
  height: 900px; /* fixed fallback height */
  border: 0;
  display: block;
  border-radius: 12px;
  overflow: auto;
`;

/* ------------------- NEW: Debug / Verify UI styles ------------------- */

const WidgetVerifyPanel = styled.div`
  margin-top: 12px;
  padding: 12px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(6px);
`;

const WidgetVerifyRow = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  align-items: center;
`;

const VerifyButton = styled.button<{ $active?: boolean }>`
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.25);
  background: ${(p) => (p.$active ? "rgba(255,255,255,0.20)" : "rgba(0,0,0,0.25)")};
  color: #fff;
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 0.18);
  }
`;

const VerifyBadge = styled.span`
  display: inline-block;
  padding: 6px 10px;
  border-radius: 999px;
  font-size: 12px;
  border: 1px solid rgba(255, 255, 255, 0.25);
  background: rgba(0, 0, 0, 0.25);
`;

const WidgetHtmlBox = styled.div`
  width: 100%;
  min-height: 400px;
  max-height: 900px;
  overflow: auto;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  background: rgba(0, 0, 0, 0.25);
  padding: 12px;
  color: #fff;

  /* IMPORTANT:
     Many fetched HTML chunks will bring their own styles. This container
     simply helps you see what actually got injected. */
`;

const Muted = styled.div`
  opacity: 0.85;
  font-size: 13px;
  line-height: 1.4;
`;

type WidgetMode = "iframe" | "html";

const BoxesPage: React.FC = () => {
  const [boxCount, setBoxCount] = useState(2);
  const contentRef = useRef<HTMLDivElement>(null);
  const featuredBoxes = useSelector((state: RootState) => state.boxes.featuredBoxes);
  const vpcBoxes = useSelector((state: RootState) => state.boxes.vpcBoxes);
  const howItWorksRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();

  /* ------------------- NEW: Widget verification state ------------------- */
  const [widgetMode, setWidgetMode] = useState<WidgetMode>("iframe");
  const [widgetHtml, setWidgetHtml] = useState<string>("");
  const [widgetLoading, setWidgetLoading] = useState<boolean>(false);
  const [widgetError, setWidgetError] = useState<string>("");

  const fetchWidgetHtml = useCallback(async () => {
    setWidgetLoading(true);
    setWidgetError("");
    try {
      // Change '/widget' if your backend route is different
      const res = await fetch("/widget", { method: "GET" });
      const text = await res.text();

      if (!res.ok) {
        throw new Error(`HTTP ${res.status}: ${text?.slice(0, 120) || "Failed to load widget HTML"}`);
      }

      setWidgetHtml(text);
    } catch (e: any) {
      setWidgetError(e?.message || "Failed to load widget HTML");
      setWidgetHtml("");
    } finally {
      setWidgetLoading(false);
    }
  }, []);

  // Auto-fetch HTML only when you switch to HTML mode (so you can verify)
  useEffect(() => {
    if (widgetMode === "html" && !widgetHtml && !widgetLoading && !widgetError) {
      fetchWidgetHtml();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [widgetMode]);

  useEffect(() => {
    const handleResize = () => {
      const windowWidth = window.innerWidth;
      if (windowWidth < 600) {
        setBoxCount(2);
      } else if (windowWidth < 768) {
        setBoxCount(3);
      } else if (windowWidth < 1024) {
        setBoxCount(4);
      } else {
        setBoxCount(6);
      }
    };

    const handleContentHeight = () => {
      if (contentRef.current) {
        const contentHeight = contentRef.current.scrollHeight;
        const windowHeight = window.innerHeight;
        const newMaxHeight = Math.min(contentHeight, windowHeight);
        contentRef.current.style.maxHeight = `${newMaxHeight}px`;
      }
    };

    window.addEventListener("resize", handleResize);
    window.addEventListener("resize", handleContentHeight);

    handleResize();
    handleContentHeight();

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("resize", handleContentHeight);
    };
  }, []);

  const scrollToHowItWorks = () => {
    if (howItWorksRef.current) {
      howItWorksRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <BoxesPageWrapper ref={contentRef}>
      <HeroComponent />

      {/* ------------------- Widget area (verify both modes) ------------------- */}
      <WidgetSection>
        <WidgetVerifyPanel>
          <WidgetVerifyRow>
            <VerifyButton $active={widgetMode === "iframe"} onClick={() => setWidgetMode("iframe")}>
              Show iframe (recommended)
            </VerifyButton>

            <VerifyButton
              $active={widgetMode === "html"}
              onClick={() => setWidgetMode("html")}
              title="Test only: HTML injection won't execute <script> tags"
            >
              Show /widget HTML (test)
            </VerifyButton>

            <VerifyButton
              onClick={() => {
                if (widgetMode === "html") fetchWidgetHtml();
              }}
              disabled={widgetMode !== "html" || widgetLoading}
              title="Reload /widget HTML"
            >
              {widgetLoading ? "Loading..." : "Reload HTML"}
            </VerifyButton>

            <VerifyBadge>
              Mode: <b>{widgetMode}</b>
            </VerifyBadge>

            {widgetMode === "html" && (
              <VerifyBadge>
                HTML chars: <b>{widgetHtml?.length || 0}</b>
              </VerifyBadge>
            )}
          </WidgetVerifyRow>

          <Spacer10 />

          {widgetMode === "iframe" ? (
            <WidgetFrame
              src="https://omegajam.com/widget/Academy"
              title="OmegaJam Academy Widget"
              allow="fullscreen; clipboard-read; clipboard-write"
            />
          ) : (
            <>
              <Muted>
                This view injects HTML from <b>/widget</b>. If the widget requires JS, it may not work here (React
                injection doesn’t execute scripts).
              </Muted>
              <Spacer10 />

              {widgetError ? (
                <WidgetHtmlBox>
                  <b style={{ color: "#ffd400" }}>Error:</b> {widgetError}
                </WidgetHtmlBox>
              ) : widgetLoading ? (
                <WidgetHtmlBox>Loading widget HTML...</WidgetHtmlBox>
              ) : widgetHtml ? (
                <WidgetHtmlBox dangerouslySetInnerHTML={{ __html: widgetHtml }} />
              ) : (
                <WidgetHtmlBox>No HTML loaded yet. Click “Reload HTML”.</WidgetHtmlBox>
              )}
            </>
          )}
        </WidgetVerifyPanel>
      </WidgetSection>

      <ButtonContainer>
        <ScrollButton onClick={scrollToHowItWorks} $gradientColors={["#ffd400", "#0af"]}>
          How It Works
        </ScrollButton>
      </ButtonContainer>

      <Spacer10 />

      <PromoSlider
        platform={Platform.BOXCHAOS}
        autoplay
        interval={5000}
        onNavigate={(to) => {
          if (to.startsWith("/promotions")) {
            dispatch(handlePageSelection("promotions"));
          }
          navigate(to);
        }}
      />

      <>
        {featuredBoxes.length > 0 && (
          <BoxesPageHeading $gradientColors={["#ffd400 0%", "#0af 100%"]}>
            {localization.featured_boxes}
          </BoxesPageHeading>
        )}
        <DynamicBoxes boxCount={boxCount} boxType={BoxType.Featured} />
      </>

      <>
        {vpcBoxes.length > 0 && (
          <BoxesPageHeading $gradientColors={["#ffd400 0%", "#0af 100%"]}>
            {localization.vpc_boxes}
          </BoxesPageHeading>
        )}
        <DynamicBoxes boxCount={boxCount} boxType={BoxType.Vpc} />
      </>

      <BoxesPageHeading $gradientColors={["#f00 0%", "#ff0 100%"]}>{localization.hot_boxes}</BoxesPageHeading>
      <DynamicBoxes boxCount={boxCount} boxType={BoxType.Hot} />

      <BoxesPageHeading $gradientColors={["#0af 0%", "#ff0 100%"]}>{localization.new_boxes}</BoxesPageHeading>
      <DynamicBoxes boxCount={boxCount} boxType={BoxType.New} />

      <div ref={howItWorksRef}>
        <HowItWorksPage isNeedToHideFooter={true} />
      </div>

      <Footer />
      <Spacer />
    </BoxesPageWrapper>
  );
};

export default BoxesPage;
